/**
 * Indicadores de salud bucal del formulario 033: higiene oral simplificada (placa, cálculo y gingivitis en seis piezas),
 * enfermedad periodontal, mal oclusión de Angle, fluorosis, y los índices CPO y ceo (que salen del odontograma).
 * Los criterios del IHOS son los de Greene y Vermillion; el profesional revisa e interpreta el resultado.
 */

/** Las seis zonas del IHOS: en cada una se examina UNA de las piezas indicadas (la permanente o, en niños, la temporal). */
export const GRUPOS_IHOS: readonly { clave: string; nombre: string; piezas: readonly number[] }[] = [
  { clave: "sd", nombre: "Molar superior derecho", piezas: [16, 17, 55] },
  { clave: "is", nombre: "Incisivo superior", piezas: [11, 21, 51] },
  { clave: "si", nombre: "Molar superior izquierdo", piezas: [26, 27, 65] },
  { clave: "ii", nombre: "Molar inferior izquierdo", piezas: [36, 37, 75] },
  { clave: "in", nombre: "Incisivo inferior", piezas: [31, 41, 71] },
  { clave: "id", nombre: "Molar inferior derecho", piezas: [46, 47, 85] },
];

export const CRITERIO_PLACA = ["0 · sin residuos ni manchas", "1 · residuos blandos hasta 1/3 de la superficie", "2 · residuos de 1/3 a 2/3 de la superficie", "3 · residuos en más de 2/3 de la superficie"] as const;
export const CRITERIO_CALCULO = ["0 · sin cálculo", "1 · cálculo supragingival hasta 1/3", "2 · supragingival de 1/3 a 2/3, o puntos subgingivales", "3 · supragingival en más de 2/3, o banda subgingival continua"] as const;
export const CRITERIO_GINGIVITIS = ["0 · ausencia", "1 · presencia"] as const;

export const GRAVEDAD = ["leve", "moderada", "severa"] as const;
/** Enfermedad periodontal y fluorosis: «no presenta» es una respuesta (distinta de no haberlo registrado) o la gravedad. */
export const ESTADOS_GRAVEDAD = ["no_presenta", ...GRAVEDAD] as const;
export type Gravedad = "" | (typeof ESTADOS_GRAVEDAD)[number];
export const CLASES_ANGLE = ["I", "II", "III"] as const;
export type ClaseAngle = "" | (typeof CLASES_ANGLE)[number];

/** Tipos de gingivitis que se pueden elegir (clasificación de enfermedades periodontales y periimplantarias de 2018). */
export const TIPOS_GINGIVITIS: readonly { clave: string; nombre: string; cie10: string }[] = [
  { clave: "placa", nombre: "Gingivitis inducida por placa bacteriana (biofilm)", cie10: "K05.1" },
  { clave: "aguda", nombre: "Gingivitis aguda", cie10: "K05.0" },
  { clave: "sistemica", nombre: "Gingivitis asociada a factores sistémicos (embarazo, pubertad, diabetes)", cie10: "K05.1" },
  { clave: "farmacos", nombre: "Agrandamiento gingival por fármacos", cie10: "K06.1" },
  { clave: "necrosante", nombre: "Gingivitis ulcerativa necrosante", cie10: "A69.1" },
];
/** Tipos de periodontitis (clasificación de 2018). Las que se estadifican llevan estadio, grado y extensión. */
export const TIPOS_PERIODONTITIS: readonly { clave: string; nombre: string; cie10: string; estadifica: boolean }[] = [
  { clave: "estadificada", nombre: "Periodontitis (con estadio y grado)", cie10: "K05.3", estadifica: true },
  { clave: "rapida", nombre: "Periodontitis de progresión rápida (grado C; antes «agresiva»)", cie10: "K05.2", estadifica: true },
  { clave: "necrosante", nombre: "Periodontitis ulcerativa necrosante", cie10: "A69.1", estadifica: false },
  { clave: "sistemica", nombre: "Periodontitis como manifestación de una enfermedad sistémica", cie10: "K05.5", estadifica: true },
  { clave: "absceso", nombre: "Absceso periodontal", cie10: "K05.2", estadifica: false },
  { clave: "endoperio", nombre: "Lesión endo-periodontal", cie10: "K05.5", estadifica: false },
  { clave: "sin_especificar", nombre: "Periodontitis sin especificar", cie10: "K05.6", estadifica: false },
];
export const ESTADIOS_PERIODONTITIS = [
  { clave: "I", nombre: "Estadio I · inicial (pérdida de inserción de 1 a 2 mm)" },
  { clave: "II", nombre: "Estadio II · moderada (3 a 4 mm)" },
  { clave: "III", nombre: "Estadio III · grave, con riesgo de perder dientes (5 mm o más)" },
  { clave: "IV", nombre: "Estadio IV · grave, con pérdida de la función masticatoria" },
] as const;
export const GRADOS_PERIODONTITIS = [
  { clave: "A", nombre: "Grado A · progresión lenta" }, { clave: "B", nombre: "Grado B · progresión moderada" }, { clave: "C", nombre: "Grado C · progresión rápida" },
] as const;
export const EXTENSIONES_PERIODONTITIS = [
  { clave: "localizada", nombre: "Localizada (menos del 30 % de los dientes)" }, { clave: "generalizada", nombre: "Generalizada (30 % o más)" }, { clave: "molar_incisivo", nombre: "Patrón molar-incisivo" },
] as const;
/** Estadios que suelen corresponder a la gravedad marcada en el formulario 033 (solo orienta: el profesional decide). */
export const ESTADIOS_SUGERIDOS: Readonly<Record<string, string>> = { leve: "I o II", moderada: "II o III", severa: "III o IV" };

export interface Periodontitis { tipo: string; estadio: "" | "I" | "II" | "III" | "IV"; grado: "" | "A" | "B" | "C"; extension: string; cuadrantes: number[] }
export const periodontitisVacia = (): Periodontitis => ({ tipo: "", estadio: "", grado: "", extension: "", cuadrantes: [1, 2, 3, 4] });

export interface FilaIhos { pieza: number | null; placa: number | null; calculo: number | null; gingivitis: number | null }
export interface IndicadoresBucales {
  /** fecha de la evaluación (aaaa-mm-dd) o vacío */
  fecha: string;
  ihos: Record<string, FilaIhos>;
  periodontal: Gravedad;
  maloclusion: ClaseAngle;
  fluorosis: Gravedad;
  /** tipo de gingivitis (si hay piezas con gingivitis); vacío = inducida por placa */
  gingivitisTipo: string;
  /** tipo, estadio, grado y cuadrantes de la periodontitis (si la enfermedad periodontal no es «no presenta») */
  periodontitis: Periodontitis;
}

export const filaIhosVacia = (): FilaIhos => ({ pieza: null, placa: null, calculo: null, gingivitis: null });
export const indicadoresVacios = (): IndicadoresBucales => ({
  fecha: "", ihos: Object.fromEntries(GRUPOS_IHOS.map((g) => [g.clave, filaIhosVacia()])), periodontal: "", maloclusion: "", fluorosis: "", gingivitisTipo: "", periodontitis: periodontitisVacia(),
});

const entero = (v: unknown, min: number, max: number): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isInteger(n) && n >= min && n <= max ? n : null;
};

/** Lo que se puede guardar: solo piezas de cada zona, valores dentro de su escala y listas conocidas. */
export function normalizarIndicadores(x: unknown): IndicadoresBucales {
  const o = x && typeof x === "object" && !Array.isArray(x) ? (x as Record<string, unknown>) : {};
  const ih = o.ihos && typeof o.ihos === "object" && !Array.isArray(o.ihos) ? (o.ihos as Record<string, unknown>) : {};
  const out = indicadoresVacios();
  for (const g of GRUPOS_IHOS) {
    const f = ih[g.clave] && typeof ih[g.clave] === "object" ? (ih[g.clave] as Record<string, unknown>) : {};
    const pieza = entero(f.pieza, 11, 85);
    out.ihos[g.clave] = { pieza: pieza !== null && g.piezas.includes(pieza) ? pieza : null, placa: entero(f.placa, 0, 3), calculo: entero(f.calculo, 0, 3), gingivitis: entero(f.gingivitis, 0, 1) };
  }
  const elegir = <T extends string>(lista: readonly T[], v: unknown): "" | T => (typeof v === "string" && (lista as readonly string[]).includes(v) ? (v as T) : "");
  out.periodontal = elegir(ESTADOS_GRAVEDAD, o.periodontal);
  out.maloclusion = elegir(CLASES_ANGLE, o.maloclusion);
  out.fluorosis = elegir(ESTADOS_GRAVEDAD, o.fluorosis);
  out.gingivitisTipo = elegir(TIPOS_GINGIVITIS.map((t) => t.clave), o.gingivitisTipo);
  const pd = o.periodontitis && typeof o.periodontitis === "object" && !Array.isArray(o.periodontitis) ? (o.periodontitis as Record<string, unknown>) : {};
  const cuad = Array.isArray(pd.cuadrantes) ? [...new Set(pd.cuadrantes.map(Number).filter((n) => Number.isInteger(n) && n >= 1 && n <= 4))].sort() : [1, 2, 3, 4];
  out.periodontitis = {
    tipo: elegir(TIPOS_PERIODONTITIS.map((t) => t.clave), pd.tipo), estadio: elegir(ESTADIOS_PERIODONTITIS.map((e) => e.clave), pd.estadio) as Periodontitis["estadio"], grado: elegir(GRADOS_PERIODONTITIS.map((g) => g.clave), pd.grado) as Periodontitis["grado"],
    extension: elegir(EXTENSIONES_PERIODONTITIS.map((e) => e.clave), pd.extension), cuadrantes: cuad.length ? cuad : [1, 2, 3, 4],
  };
  out.fecha = typeof o.fecha === "string" && /^\d{4}-\d{2}-\d{2}$/.test(o.fecha) && !Number.isNaN(Date.parse(o.fecha + "T12:00:00Z")) ? o.fecha : "";
  return out;
}

export const hayIndicadores = (i: IndicadoresBucales) =>
  !!(i.periodontal || i.maloclusion || i.fluorosis || Object.values(i.ihos).some((f) => f.placa !== null || f.calculo !== null || f.gingivitis !== null));

export type NivelIhos = "buena" | "regular" | "mala";
export interface ResumenIhos {
  /** piezas con placa y cálculo registrados (de 6) */
  evaluadas: number;
  sumaPlaca: number; sumaCalculo: number; sumaGingivitis: number;
  promedioPlaca: number | null; promedioCalculo: number | null;
  /** promedio de placa + promedio de cálculo (0 a 6) */
  ihos: number | null;
  nivel: NivelIhos | null; nivelPlaca: NivelIhos | null; nivelCalculo: NivelIhos | null;
  /** piezas con gingivitis registrada y con gingivitis presente */
  gingivitisRegistradas: number; conGingivitis: number;
}

const redondear = (n: number) => Math.round(n * 100) / 100;
/** Placa o cálculo por separado (escala 0 a 3): 0–0,6 buena, 0,7–1,8 regular, 1,9–3,0 mala. */
const nivelComponente = (n: number): NivelIhos => (n <= 0.6 ? "buena" : n <= 1.8 ? "regular" : "mala");
/** IHOS (escala 0 a 6): 0–1,2 buena, 1,3–3,0 regular, 3,1–6,0 mala. */
const nivelTotal = (n: number): NivelIhos => (n <= 1.2 ? "buena" : n <= 3 ? "regular" : "mala");

export function resumenIhos(i: IndicadoresBucales): ResumenIhos {
  const filas = Object.values(i.ihos);
  const completas = filas.filter((f) => f.placa !== null && f.calculo !== null);
  const sumaPlaca = completas.reduce((s, f) => s + (f.placa as number), 0);
  const sumaCalculo = completas.reduce((s, f) => s + (f.calculo as number), 0);
  const n = completas.length;
  const promedioPlaca = n ? redondear(sumaPlaca / n) : null, promedioCalculo = n ? redondear(sumaCalculo / n) : null;
  const ihos = promedioPlaca !== null && promedioCalculo !== null ? redondear(promedioPlaca + promedioCalculo) : null;
  const ging = filas.filter((f) => f.gingivitis !== null);
  return {
    evaluadas: n, sumaPlaca, sumaCalculo, sumaGingivitis: ging.reduce((s, f) => s + (f.gingivitis as number), 0),
    promedioPlaca, promedioCalculo, ihos,
    nivel: ihos === null ? null : nivelTotal(ihos),
    nivelPlaca: promedioPlaca === null ? null : nivelComponente(promedioPlaca),
    nivelCalculo: promedioCalculo === null ? null : nivelComponente(promedioCalculo),
    gingivitisRegistradas: ging.length, conGingivitis: ging.filter((f) => f.gingivitis === 1).length,
  };
}

/** Qué índice corresponde según la dentición (que sale de la edad): temporal → ceo; mixta → CPO y ceo; permanente → CPO. */
export function indicesPorDenticion(d: "temporal" | "mixta" | "permanente"): { cpo: boolean; ceo: boolean } {
  return { cpo: d !== "temporal", ceo: d !== "permanente" };
}

const coma = (n: number) => String(n).replace(".", ",");
/** Resumen en texto para la historia completa. */
export function textoIndicadores(i: IndicadoresBucales): string {
  const r = resumenIhos(i);
  return [
    r.ihos !== null ? `Higiene oral simplificada ${coma(r.ihos)} (${r.nivel}): placa ${coma(r.promedioPlaca!)}, cálculo ${coma(r.promedioCalculo!)}, en ${r.evaluadas} de 6 piezas.` : "",
    r.gingivitisRegistradas ? `Gingivitis en ${r.conGingivitis} de ${r.gingivitisRegistradas} piezas.` : "",
    i.periodontal === "no_presenta" ? "No presenta enfermedad periodontal." : i.periodontal ? `Enfermedad periodontal ${i.periodontal}.` : "",
    i.maloclusion ? `Mal oclusión de Angle clase ${i.maloclusion}.` : "",
    i.fluorosis === "no_presenta" ? "No presenta fluorosis." : i.fluorosis ? `Fluorosis ${i.fluorosis}.` : "",
  ].filter(Boolean).join(" ");
}

/* ---------- diagnósticos que salen solos de los indicadores ---------- */

/** Las notas de los diagnósticos automáticos empiezan así: así se reconocen para reemplazarlos al volver a guardar la historia. */
export const MARCA_AUTO = "Automático (IHOS): ";
export interface DiagnosticoAuto { cie10: string; nota: string }

const plural = (n: number, uno: string, varios: string) => `${n} ${n === 1 ? uno : varios}`;

/**
 * Diagnósticos generales que se derivan del formulario: placa o cálculo mayores a 0 (depósitos en los dientes, K03.6), piezas con gingivitis
 * (según su tipo) y enfermedad periodontal (según el tipo de periodontitis elegido). Cada uno trae el tratamiento sugerido en el plan
 * (profilaxis dental profunda; raspado y alisado radicular por cuadrante).
 */
export function diagnosticosDeIndicadores(i: IndicadoresBucales): DiagnosticoAuto[] {
  const out: DiagnosticoAuto[] = [];
  const r = resumenIhos(i);
  const filas = Object.values(i.ihos);
  const conPlaca = filas.filter((f) => (f.placa ?? 0) > 0).length, conCalculo = filas.filter((f) => (f.calculo ?? 0) > 0).length;
  if (conPlaca || conCalculo) {
    const que = [conPlaca ? `placa bacteriana en ${plural(conPlaca, "pieza", "piezas")}` : "", conCalculo ? `cálculo (acreciones) en ${plural(conCalculo, "pieza", "piezas")}` : ""].filter(Boolean).join(" y ");
    out.push({ cie10: "K03.6", nota: `${MARCA_AUTO}Presencia de ${que}${r.ihos !== null ? `. IHOS ${coma(r.ihos)} (${r.nivel})` : ""}.` });
  }
  if (r.conGingivitis > 0) {
    const t = TIPOS_GINGIVITIS.find((x) => x.clave === i.gingivitisTipo) ?? TIPOS_GINGIVITIS[0];
    out.push({ cie10: t.cie10, nota: `${MARCA_AUTO}${t.nombre} en ${r.conGingivitis} de ${r.gingivitisRegistradas} piezas examinadas.` });
  }
  if (i.periodontal && i.periodontal !== "no_presenta") {
    const p = i.periodontitis;
    const t = TIPOS_PERIODONTITIS.find((x) => x.clave === p.tipo) ?? TIPOS_PERIODONTITIS[0];
    const detalle = t.estadifica
      ? [p.estadio ? `estadio ${p.estadio}` : "", p.grado ? `grado ${p.grado}` : "", EXTENSIONES_PERIODONTITIS.find((e) => e.clave === p.extension)?.nombre.split(" (")[0].toLowerCase() ?? ""].filter(Boolean).join(", ")
      : "";
    out.push({ cie10: t.cie10, nota: `${MARCA_AUTO}${t.nombre}${detalle ? `: ${detalle}` : ""}. Gravedad ${i.periodontal}. Cuadrantes: ${(p.cuadrantes.length ? p.cuadrantes : [1, 2, 3, 4]).join(", ")}.`.slice(0, 200) });
  }
  return out;
}

/** Cuántos cuadrantes dice una nota de periodontitis («Cuadrantes: 1, 2, 4.»); null si no lo dice. */
export function cuadrantesDeNota(nota: string): number | null {
  const m = nota.match(/Cuadrantes: ([1-4](?:, [1-4])*)\./);
  return m ? new Set(m[1].split(", ")).size : null;
}

/** Tratamientos que el plan sugiere para cada diagnóstico automático (con los nombres del arancel). */
export function tratamientosSugeridos(d: DiagnosticoAuto): string[] {
  const n = cuadrantesDeNota(d.nota) ?? 4;
  switch (d.cie10) {
    case "K03.6": return ["Profilaxis dental profunda (destartraje y pulido)"];
    case "K05.0": case "K05.1": return ["Profilaxis dental profunda (destartraje y pulido)", "Instrucción de higiene oral y control de placa"];
    case "K06.1": return ["Profilaxis dental profunda (destartraje y pulido)", "Instrucción de higiene oral y control de placa", "Gingivectomía o gingivoplastia"];
    case "A69.1": return ["Desbridamiento de gingivitis o periodontitis necrosante", "Instrucción de higiene oral y control de placa"];
    case "K05.2": case "K05.3": case "K05.4": case "K05.5": case "K05.6":
      return [`Raspado y alisado radicular (${n} cuadrante${n === 1 ? "" : "s"})`, ...(/absceso/i.test(d.nota) ? ["Drenaje y tratamiento de absceso periodontal"] : []), "Mantenimiento periodontal"];
    default: return [];
  }
}
